// Favoris Marketplace
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Package, ShoppingCart, Trash2, Bell, BellOff, Star, Eye, ArrowLeft, Check, X, MessageCircle, User, MapPin } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { TicketModal } from '../../../components/TicketModal';

function FavoriCard({ favori, onRemove, onToggleAlert, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full"
    >
      <div 
        className="relative flex-shrink-0 cursor-pointer"
        onClick={onClick}
      >
        <div className="h-40 bg-gray-100">
          {favori.image ? (
            <img src={favori.image} alt={favori.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={40} className="text-gray-300" />
            </div>
          )}
        </div>
        {favori.promo && (
          <span className="absolute top-2 left-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded">
            -{favori.promo}%
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(favori.id); }}
          className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-white rounded-full shadow text-red-500"
        >
          <Heart size={18} className="fill-red-500" />
        </button>
      </div>
      
      <div className="p-4 flex flex-col flex-1">
        {/* Zone titre - hauteur fixe pour 2 lignes */}
        <div 
          className="h-10 mb-1 cursor-pointer"
          onClick={onClick}
        >
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight hover:text-orange-600 transition-colors">{favori.title}</h3>
        </div>
        <p className="text-xs text-gray-500 mb-2">{favori.seller}</p>
        
        {/* Zone prix */}
        <div className="flex items-center gap-2 mb-2">
          {favori.oldPrice && (
            <span className="text-xs text-gray-400 line-through">{favori.oldPrice} €</span>
          )}
          <span className="text-base font-semibold text-[#1E3A5F]">{favori.price} €</span>
        </div>

        {/* Zone avis */}
        <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
          <Star size={12} className="text-yellow-500 fill-yellow-500" />
          <span>{favori.rating}</span>
          <span>({favori.reviews} avis)</span>
        </div>

        {/* Spacer pour pousser les boutons en bas */}
        <div className="flex-1" />

        {/* Boutons - toujours en bas */}
        <div className="flex items-center gap-2">
          <Button
            onClick={onClick}
            className="flex-1 bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white text-xs"
            size="sm"
          >
            <Eye size={12} className="mr-1" /> Voir
          </Button>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleAlert(favori.id); }}
            className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center ${
              favori.alerteActive 
                ? 'bg-orange-100 text-orange-600' 
                : 'bg-gray-100 text-gray-400 hover:text-gray-600'
            }`}
            title={favori.alerteActive ? 'Alerte prix active' : 'Activer l\'alerte prix'}
          >
            {favori.alerteActive ? <Bell size={16} /> : <BellOff size={16} />}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function FavorisMarketplace({ onSelectTool }) {
  const [favoris, setFavoris] = useState([
    { id: 1, title: 'Perceuse-visseuse Bosch Professional 18V', seller: 'BricoTools Pro', sellerType: 'pro', available: true, price: 189, oldPrice: 229, promo: 17, rating: 4.8, reviews: 124, alerteActive: true, image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400' },
    { id: 2, title: 'Niveau laser Bosch GLL 3-80', seller: 'OutillagePro', sellerType: 'pro', available: true, price: 299, rating: 4.9, reviews: 89, alerteActive: false, image: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=400' },
    { id: 3, title: 'Scie circulaire Makita 185mm', seller: 'BricoTools Pro', sellerType: 'pro', available: false, price: 245, rating: 4.7, reviews: 56, alerteActive: true, image: null },
    { id: 4, title: 'Lot de tournevis isolés 1000V', seller: 'ElectroPro', sellerType: 'pro', available: true, price: 45, oldPrice: 59, promo: 24, rating: 4.6, reviews: 203, alerteActive: false, image: 'https://images.unsplash.com/photo-1581147036324-c17ac41f3f88?w=400' },
  ]);
  
  // Popup states
  const [selectedFavori, setSelectedFavori] = useState(null);
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);

  const handleRemove = (id) => {
    setFavoris(prev => prev.filter(f => f.id !== id));
  };

  const handleToggleAlert = (id) => {
    setFavoris(prev => prev.map(f => 
      f.id === id ? { ...f, alerteActive: !f.alerteActive } : f
    ));
  };

  // Contacter le vendeur → Ouvrir le modal Ticket
  const handleContactSeller = () => {
    setShowUnavailable(false);
    setShowTicketModal(true);
  };
  
  // Ajouter au panier
  const handleAddToCart = () => {
    if (!selectedFavori?.available) {
      setShowUnavailable(true);
      setTimeout(() => {
        setShowUnavailable(false);
      }, 2500);
    } else {
      setShowCartSuccess(true);
      setTimeout(() => {
        setShowCartSuccess(false);
        setSelectedFavori(null);
      }, 2000);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onSelectTool && (
            <button 
              onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Retour au tableau de bord"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
          )}
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Favoris</h1>
            <p className="text-xs sm:text-sm text-gray-500">{favoris.length} produit{favoris.length > 1 ? 's' : ''} sauvegardé{favoris.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {favoris.filter(f => f.alerteActive).length} alerte{favoris.filter(f => f.alerteActive).length > 1 ? 's' : ''} active{favoris.filter(f => f.alerteActive).length > 1 ? 's' : ''}
        </div>
      </div>

      {favoris.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Heart size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun favori</h3>
          <p className="text-gray-500 mb-4">Sauvegarde des produits pour les retrouver facilement</p>
          <Link to="/produits">
            <Button className="bg-blue-500 hover:bg-blue-600 text-white">
              Parcourir la marketplace
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {favoris.map(favori => (
            <FavoriCard
              key={favori.id}
              favori={favori}
              onRemove={handleRemove}
              onToggleAlert={handleToggleAlert}
              onClick={() => setSelectedFavori(favori)}
            />
          ))}
        </div>
      )}
      
      {/* Popup Détails du favori */}
      <AnimatePresence>
        {selectedFavori && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setSelectedFavori(null); setShowUnavailable(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
            >
              {showUnavailable ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Article plus disponible</h3>
                  <p className="text-sm text-gray-500 mb-4">Cet article a déjà été vendu et n'est plus disponible à l'achat.</p>
                  <Button
                    onClick={handleContactSeller}
                    variant="outline"
                    className="mx-auto"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Contacter le vendeur
                  </Button>
                </div>
              ) : showCartSuccess ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Ajouté au panier !</h3>
                  <p className="text-sm text-gray-500">{selectedFavori?.title} a été ajouté à ton panier.</p>
                </div>
              ) : (
                <>
                  {/* Header avec image */}
                  <div className="relative">
                    <div className="h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
                      {selectedFavori.image ? (
                        <img 
                          src={selectedFavori.image} 
                          alt={selectedFavori.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={48} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedFavori(null)}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
                    >
                      <X size={18} className="text-gray-600" />
                    </button>
                    {/* Badge promo */}
                    {selectedFavori.promo && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                        -{selectedFavori.promo}%
                      </span>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="p-5">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{selectedFavori.title}</h2>
                    
                    {/* Prix */}
                    <div className="flex items-center gap-2 mb-4">
                      {selectedFavori.oldPrice && (
                        <span className="text-sm text-gray-400 line-through">{selectedFavori.oldPrice} €</span>
                      )}
                      <span className="text-2xl font-bold text-[#1E3A5F]">{selectedFavori.price} €</span>
                    </div>

                    {/* Avis */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map((star) => (
                          <Star 
                            key={star} 
                            size={14} 
                            className={star <= Math.round(selectedFavori.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} 
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{selectedFavori.rating}</span>
                      <span className="text-sm text-gray-500">({selectedFavori.reviews} avis)</span>
                    </div>

                    {/* Infos vendeur */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={18} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vendu par</p>
                          <p className="font-medium text-gray-900">{selectedFavori.seller}</p>
                        </div>
                        {selectedFavori.alerteActive && (
                          <div className="ml-auto">
                            <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                              <Bell size={12} />
                              Alerte active
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <Button
                        onClick={handleAddToCart}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2"
                      >
                        <ShoppingCart size={16} />
                        Ajouter au panier
                      </Button>
                      <Button
                        onClick={handleContactSeller}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={16} />
                        Contacter le vendeur
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Ticket (composant réutilisable) */}
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedFavori(null); }}
        recipientName={selectedFavori?.seller}
        recipientId={selectedFavori?.id}
        type="buyer"
        onSuccess={() => setSelectedFavori(null)}
      />
    </div>
  );
}
